import { VETERAN_DEVELOPMENT_LABELS } from './botDevelopment.js';
// ---------------------------------------------------------------------------
// Every balance lever lives here. Nothing below this file should hard-code a
// number that a designer might want to turn.
//
// The numbers below are the code's defaults. tuning.json at the repository
// root overrides them wherever it has a value, and it is what the game plays:
// the server applies it at start-up and hands it to every client, and balance
// runs, scenarios and the manual apply it too (tuning.ts). To change a rule's
// number, change it there. The single-number rules are `let` so that tuning
// reaches every module that imports them.
// ---------------------------------------------------------------------------
export const BOT_SKILL_LABELS = {
    RECRUIT: 'Recruit',
    REGULAR: 'Regular',
    VETERAN: 'Veteran',
    VETERAN_OFFENSIVE: 'Veteran (offensive)',
    VETERAN_NOSHELTER: 'veteran-noshelter',
    VETERAN_INDUSTRY: 'Veteran (industry)',
    RECRUIT_FILL: 'Recruit (full establishment)',
    REGULAR_FILL: 'Regular (full establishment)',
    VETERAN_FILL: 'Veteran (full establishment)',
    MASTER: 'Master',
    MASTER_PAIR: 'Master (two targets)',
    JEV_SMART: 'Jev (smart)',
    JEV_SMARTER: 'Jev (smarter)',
    JEV_SMARTEST: 'Jev (smartest)',
    ...VETERAN_DEVELOPMENT_LABELS,
};
export const CITY_W = 24;
export const CITY_H = 24;
/** Scalar default for explicit custom rooms and simulation controls. */
export const PHASE_SECONDS = 120;
/** Standing-war decision clocks; Resolution is automatic work. */
export const WAR_PHASE_DURATIONS = { PLAN: 60, ATTACK: 90, DEFEND: 90 };
export const INACTIVE_PHASE_LIMIT = 3;
/**
 * How long a round will wait for everyone to finish watching their replay
 * before starting the planning clock anyway.
 *
 * The hold exists for fairness: a forty-round salvo takes most of a minute to
 * play and a two-round one is over in seconds, so whoever had the quiet round
 * would otherwise be planning while their opponent is still watching. The cap
 * exists because one player alt-tabbing must not be able to stop the match.
 * Three minutes, because a round has two replays: your salvo, then theirs.
 */
export const REPLAY_HOLD_MAX_MS = 180_000;
// --- command training (docs/interface/TRAINING.md) ------------------------------------------
/** Rounds in a training match: one to be shown everything, two to try it. */
export const TRAINING_ROUNDS = 3;
/** The training board, the same every time, so the steps can count on what is on it. */
export const TRAINING_SEED = 'command-training-1';
/**
 * Training has no phase clock: a phase waits for its commander. A room nobody
 * has touched for this long is given up.
 */
export const TRAINING_IDLE_MS = 30 * 60_000;
/** Map complex extent in km. Node spacing is derived from this. */
export const MAP_W_KM = 3000;
export const MAP_H_KM = 1800;
/**
 * Identical for every city in the match, which is what makes the start fair.
 * Only the *layout* is randomised.
 */
export const ASSET_SPECS = {
    // military
    command: { cls: 'military', count: 1, w: 2, h: 2, hp: 220, pop: 0, label: 'Command Bunker', repairPerHp: 1.6 },
    airbase: { cls: 'military', count: 1, w: 4, h: 2, hp: 180, pop: 0, label: 'Airbase', repairPerHp: 1.2 },
    silo: { cls: 'military', count: 2, w: 1, h: 1, hp: 160, pop: 0, label: 'Missile Silo', repairPerHp: 1.4 },
    radar: { cls: 'military', count: 2, w: 1, h: 1, hp: 90, pop: 0, label: 'Radar Station', repairPerHp: 1.5 },
    barracks: { cls: 'military', count: 2, w: 2, h: 1, hp: 140, pop: 0, label: 'Barracks', repairPerHp: 1.0 },
    depot: { cls: 'military', count: 2, w: 2, h: 1, hp: 110, pop: 0, label: 'Munitions Depot', repairPerHp: 1.0 },
    // civilian
    residential: { cls: 'civilian', count: 8, w: 2, h: 2, hp: 100, pop: 0, label: 'Housing Block', repairPerHp: 0.8 },
    power_plant: { cls: 'civilian', count: 2, w: 2, h: 2, hp: 150, pop: 0, label: 'Power Plant', repairPerHp: 1.3 },
    hospital: { cls: 'civilian', count: 2, w: 2, h: 1, hp: 110, pop: 0, label: 'Hospital', repairPerHp: 1.1 },
    factory: { cls: 'civilian', count: 3, w: 2, h: 2, hp: 130, pop: 0, label: 'Factory', repairPerHp: 1.0 },
    refinery: { cls: 'civilian', count: 1, w: 2, h: 2, hp: 140, pop: 0, label: 'Refinery', repairPerHp: 1.2 },
    water_works: { cls: 'civilian', count: 1, w: 2, h: 2, hp: 120, pop: 0, label: 'Water Works', repairPerHp: 1.2 },
    farm: { cls: 'civilian', count: 2, w: 3, h: 3, hp: 70, pop: 0, label: 'Agricultural', repairPerHp: 0.5 },
    comms: { cls: 'civilian', count: 1, w: 1, h: 1, hp: 80, pop: 0, label: 'Broadcast Mast', repairPerHp: 1.1 },
};
export const ALL_ASSET_TYPES = Object.keys(ASSET_SPECS);
export const WEAPON_SPECS = {
    recon_drone: {
        label: 'Recon Drone',
        /**
         * As far as a ballistic missile reaches.
         *
         * It used to be the shortest-legged thing in the arsenal at 900km, which
         * on a four-a-side board meant you could shoot at three cities and scout
         * exactly one — every other target had to be attacked blind or scouted by
         * hitting it. That is backwards both for play and for the thing itself: a
         * reconnaissance drone's whole character is endurance, and one can loiter
         * for days. It is still the easiest kill on the board, so the range buys
         * you the attempt, not the picture.
         */
        rangeKm: 2500,
        alt: 'low',
        damage: 0,
        splash: 0,
        baseCep: 0,
        cost: { materials: 3, fuel: 2, credits: 1 },
        reconRadius: 3,
        arrival: 2,
        slots: 1,
        blurb: 'Sees. Does not shoot. Your CEP is a lie without one.',
    },
    strike_drone: {
        label: 'Strike Drone',
        // Enough to dogleg onto a third face of the nearest enemy from most cities
        // (owner, 20 October 2026; scripts/analysis/bearings.ts). The board was
        // solved at 600 with its second enemy kept beyond 675, so a drone still
        // reaches one enemy of four; past that it would reach two.
        rangeKm: 650,
        alt: 'low',
        // 14 -> 18 with the cruise missile's 35 -> 41 and the ballistic missile's
        // 78 -> 60 (owner, 21 October 2026; D28): damage moved off the one weapon
        // a whole side can mass, the pace of Veteran battles unchanged. 18 -> 20
        // with the cruise missile's 41 -> 45 as the ballistic missile came down
        // again (owner, 21 October 2026; D34).
        damage: 20,
        splash: 0,
        baseCep: 1.0,
        cost: { materials: 4, fuel: 2, credits: 1 },
        reconRadius: 1,
        arrival: 4,
        slots: 1,
        blurb: 'Cheap and attritable, but it has to be launched from close in.',
    },
    cruise: {
        label: 'Cruise Missile',
        rangeKm: 1400,
        alt: 'low',
        // Was 35, then 41 (D28, D34; see the strike drone).
        damage: 45,
        splash: 1,
        baseCep: 0.6,
        cost: { materials: 10, fuel: 4, credits: 5 },
        reconRadius: 1,
        arrival: 4,
        slots: 3,
        blurb: 'The precision option. Flies low, so point defence gets a say.',
    },
    ballistic: {
        label: 'Ballistic Missile',
        rangeKm: 2500,
        alt: 'high',
        // Was 78, then 60 (D28). Line for line it did twice what a cruise missile
        // line did and four times a drone line, and a whole cluster could mass it
        // on one city, the only weapon that reaches all four; at 50 with a blast of
        // 1.5 (D34, the owner) a round carries about 1.8 times a cruise missile's
        // blast, not 2.9. Kept below the ICBM's reach and blast.
        damage: 50,
        splash: 1.5,
        // Was 2.4 — a terminal stage that arrives at Mach 8 is not a weapon you
        // walk onto a target, it is one you point. The whole reason to own the
        // silos is that this lands where it was sent.
        baseCep: 0.9,
        cost: { materials: 26, fuel: 11, credits: 18 },
        reconRadius: 2,
        arrival: 3,
        // Two to a wave of full silos, with room for one cruise missile beside
        // them, and four going all out (D34, the owner): the slots are the ration,
        // in place of D31's flat cap. Was 7, which held three.
        slots: 9,
        blurb: 'Fast, precise and very hard to stop. The heavy hammer, priced like one and rationed by the wave.',
    },
    icbm: {
        label: 'ICBM',
        /**
         * Reaches anywhere on any board this game can generate.
         *
         * This is the one weapon the pocket system does not contain, and that is
         * the whole of its design. Every other range in the table is a statement
         * about which cities are your problem; a board is laid out so that the
         * cities inside a pocket can reach each other and nothing outside can reach
         * in. An ICBM ignores that. The furthest two cities on the largest board
         * the generator will build are about 23,000km apart, so the figure is set
         * well clear of it rather than special-cased as infinity — a finite range
         * keeps the routing, the fuel arithmetic and the over-reach warning all
         * working exactly as they do for everything else.
         *
         * It is deliberately excluded from the reach that sizes pockets. See
         * `longestPocketReachKm` in citygen.ts: including it would push every
         * pocket in the theatre a continent apart to preserve a guarantee this
         * weapon exists to break.
         */
        rangeKm: 60000,
        alt: 'high',
        // Deliberately not a bigger ballistic missile. Its job is picking off a city
        // that is already failing, usually with several players pooling onto it —
        // and a heavy single-point warhead is wasted on a wreck, because the assets
        // left standing have less hit-point left than the warhead throws away. So
        // the ICBM trades punch for footprint: it sweeps a district rather than
        // flattening one building, which is worth more the less is left standing.
        // 58 -> 48 when the ballistic missile came down to 50 (D34): still lighter.
        damage: 48,
        splash: 3,
        /**
         * Sixty per cent of the way from the cruise missile to the ballistic one.
         *
         * Worth knowing which way that runs in this game: the cruise missile is the
         * precise one at 0.6 and the ballistic is the blunter at 0.9, so this lands
         * at 0.78 — slightly *better* than a ballistic missile rather than slightly
         * worse. What stops that obsoleting the silo fleet is not accuracy but rate:
         * one built per action point, one launched per wave.
         */
        baseCep: 0.78,
        cost: { materials: 44, fuel: 20, credits: 32 },
        reconRadius: 2,
        arrival: 1,
        slots: 9,
        blurb: 'Reaches any city on the board. One a wave, and you feel every one.',
    },
};
/** At most this many ICBMs may be launched in a single wave. */
export let ICBM_PER_WAVE = 1;
/**
 * At most this many ballistic missiles may be launched in a single wave, a
 * device among them, whatever the silos' slots would hold. Going all out
 * doubles it, as it doubles the slots (`ballisticsPerWave`). 0 sets no cap:
 * the slots are then the only limit.
 *
 * 0 (D34, 21 October 2026): the ballistic missile's own slots now hold a wave
 * to two, four all out. It was 2 under D31, when its slots held three.
 */
export let BALLISTIC_PER_WAVE = 0;
export const ALL_WEAPONS = Object.keys(WEAPON_SPECS);
/**
 * Warheads are separate from the thing that carries them. Any of these can be
 * fitted to any missile or strike drone, which means a cheap cruise missile with
 * a recovered thermobaric warhead is a genuinely different weapon from the one
 * you started the war with.
 */
export const WARHEAD_SPECS = {
    standard: {
        label: 'Standard HE',
        damage: 1, splash: 0, casualties: 1, fear: 0, hardened: 1, vsDefence: 1,
        weight: 0,
        slots: 0,
        blurb: 'What everything ships with. Unlimited, and never the reason you won.',
    },
    he: {
        label: 'Heavy Blast',
        damage: 1.55, splash: 0, casualties: 0.8, fear: 2, hardened: 1.25, vsDefence: 0.9,
        weight: 40,
        slots: 0,
        blurb: 'More explosive, tighter pattern. The straightforward answer to concrete.',
    },
    cluster: {
        label: 'Cluster',
        // Splash 5 is the whole point of the round: it is not a bigger bang, it is
        // the same bang spread over a district. Across a belt of launchers or a
        // row of housing it touches everything at once.
        damage: 0.7, splash: 5, casualties: 1.5, fear: 6, hardened: 0.3, vsDefence: 3.4,
        weight: 30,
        slots: 0,
        blurb: 'Wide. Useless against a bunker, strong against a spread-out SAM belt. Over housing it kills many more people.',
    },
    thermobaric: {
        label: 'Thermobaric',
        damage: 1.9, splash: 1, casualties: 2.4, fear: 12, hardened: 1.9, vsDefence: 1.1,
        weight: 16,
        slots: 0,
        blurb: 'Burns the air out of enclosed spaces. In housing it kills several times as many people as a standard warhead.',
    },
    chemical: {
        label: 'Chemical',
        damage: 0.05, splash: 3, casualties: 7, fear: 30, hardened: 0.1, vsDefence: 0.2,
        weight: 10,
        slots: 0,
        blurb: 'Leaves the buildings standing and poisons the people in them. The city is out of the war '
            + 'for a round: half accuracy, half its air defence. Deaths are many times a standard warhead.',
    },
    nuclear: {
        label: 'Nuclear',
        // Splash 13 on a 24-tile city is the point: this is not a strike on a
        // district, it is a strike on the city. Anything short of covering most of
        // the map leaves an enemy standing at two-thirds strength, which is not
        // what a device is for.
        damage: 6, splash: 13, casualties: 9, fear: 60, hardened: 2.5, vsDefence: 3,
        // 3, from 1 (D30, 21 October 2026): at 1 a 4v4 battle found a device one
        // time in thirteen and a 16-a-side war one time in three, so the owner
        // never saw one; at 3 a war finds about one.
        weight: 3,
        slots: 0,
        blurb: 'Destroys most of a city and nearly everyone in it. Found, never built: one device a find, and gone when fired.',
    },
};
export const ALL_WARHEADS = Object.keys(WARHEAD_SPECS);
/**
 * Extra casualty multiplier when a thermobaric round comes down on housing.
 *
 * A fuel-air weapon does its worst work in enclosed occupied space, and this is
 * the one place the model should say so loudly. It does not touch the damage to
 * the structure — the block is left standing and empty, which is a different and
 * worse thing than the block being flattened.
 */
export let THERMOBARIC_RESIDENTIAL_CASUALTIES = 2.5;
// --- battlefield finds ------------------------------------------------------
/**
 * Special warheads come only from the battlefield. Every successful hit gives
 * the attacker one roll, and every incoming round that came down outside the
 * defender's city (a dud that flew) gives the defender one. A roll that finds
 * something rolls the table by WARHEAD_SPECS weight (40/30/16/10/3), then a
 * stash of DISCOVERY_STASH_MIN..MAX of that warhead — or exactly one, for a
 * nuclear device. Nothing found is ever a recipe.
 */
export let DISCOVERY_CHANCE_HIT = 1 / 60;
export let DISCOVERY_CHANCE_MISS = 1 / 60;
/** Nuclear devices cannot be found before this round; conventional finds remain available. */
export let NUCLEAR_DISCOVERY_START_ROUND = 10;
export let DISCOVERY_STASH_MIN = 1;
export let DISCOVERY_STASH_MAX = 5;
// --- salvage ---------------------------------------------------------------
/**
 * A munition that lands without going off belongs to whoever owns the ground.
 * Drones come down largely intact; a ballistic missile arrives as confetti.
 */
export const SALVAGE_CHANCE = {
    recon_drone: 0.55,
    strike_drone: 0.45,
    cruise: 0.28,
    ballistic: 0.1,
    // Arrives faster and from higher than anything else. There is nothing left.
    icbm: 0.06,
};
/** Fraction of the munition material cost recovered as scrap. */
export let SALVAGE_MATERIAL_FRACTION = 0.65;
/**
 * What a round that was merely turned away is worth, against one that came
 * down in your streets.
 *
 * A jammer does not bring anything down — it breaks the seeker and the round
 * flies off to land somewhere that is not the aimpoint, and quite possibly not
 * your ground at all. It was paying the same as a dud recovered intact, which
 * made electronic warfare the best salvage yard in the game and got worse the
 * better the jammers got.
 */
export let SALVAGE_DIVERT_MULT = 0.2;
/** An intercepted round is wreckage rather than a prize, but not worthless. */
export let SALVAGE_INTERCEPT_MULT = 0.35;
/**
 * Launch slots available in one wave at full health. Read against WEAPON_SPECS
 * slot costs: a full-strength airbase can put up ten recon drones or five strike
 * drones in a wave, and intact silos can manage four cruise missiles — or one
 * ballistic missile and one cruise, and nothing else.
 */
export let AIRBASE_SLOTS = 16;
export let SILO_SLOTS = 21;
/**
 * How much conventional ammunition a city can hold: this many healthy waves of
 * each pool, counted in launch slots. Drones share AIRBASE_SLOTS x this, and
 * cruise, ballistic and ICBM share SILO_SLOTS x this, so storing a mix never
 * gets round the cap. It is the healthy figure on purpose: a damaged airbase
 * launches less, but the stock in the sheds is not lost with it.
 */
export let STOCKPILE_WAVES = 5;
/**
 * How many units one production order turns out.
 *
 * A production order is a *line*, not a unit: the factory floor is measured in
 * how many things it can have in build at once, and what comes off the end of
 * each line depends on what it is. Drones roll off in batches; a ballistic
 * missile is one object that a whole line spends the phase on.
 */
export const PRODUCTION_BATCH = {
    recon_drone: 4,
    // Strike drones 5 -> 4 and cruise 3 -> 2 in Stage 7 of the strategic layer
    // (variant B; docs/archive/gameplay/STRATEGIC_LAYER.md): a leaner stockpile, which rewarded the
    // better commander most.
    strike_drone: 4,
    cruise: 2,
    ballistic: 1,
    // A line spends the whole phase on one of these, same as a ballistic missile.
    icbm: 1,
};
/** Defences are built one to a line, like the heaviest missiles. */
export let DEFENCE_BATCH = 1;
/**
 * Every production order gets a roll for a good run: tooling that behaves,
 * a shift that goes right, a batch that comes through inspection clean.
 *
 * This exists to take the straight line out of the economy. Two commanders
 * spending identically should not arrive at identical arsenals, and a run of
 * luck on the drone line is the sort of thing you can build a phase around.
 */
export let PRODUCTION_SURGE_CHANCE = 0.25;
export let PRODUCTION_SURGE_MULT = 1.5;
// --- waves -----------------------------------------------------------------
/** Waves you can split a salvo across before any development. */
export let BASE_WAVES = 2;
/** What attack_2 adds. */
export let WAVE_DOCTRINE_BONUS = 2;
/**
 * Below this command integrity you cannot coordinate more than a single wave.
 * A wrecked headquarters cannot run a timed multi-axis attack.
 */
export let MULTI_WAVE_COMMAND_FLOOR = 0.4;
// --- ammunition ------------------------------------------------------------
/** Magazine depth per launcher type. Shots per wave are drawn from this. */
export const DEFENCE_AMMO = {
    // Two full-rate waves: the volume layer runs dry in a long attack (18
    // October 2026, owner's direction; it held three).
    ciws: 20,
    sam_short: 24,
    sam_long: 6,
    ew_jammer: 0,
    shelter: 0,
};
/** Rounds one Produce ammunition batch moves from the depots to the launchers. */
export let REARM_BASE = 15;
/** Cost per round moved. */
export const REARM_COST_PER_ROUND = { materials: 3, fuel: 0, credits: 2 };
export let REARM_AP = 1;
// --- the damage curve ------------------------------------------------------
/**
 * capability = 1 - (1 - integrity) ^ RESILIENCE_EXP
 *
 * The shape the whole game hangs on. Above 1 it makes light damage nearly free
 * and heavy damage catastrophic: 90% intact still yields 98% capability, 50%
 * yields 71%, 20% yields 33%, 10% yields 17%. Two evenly matched cities trade
 * blows on a flat curve for a long time, and the moment one is genuinely behind,
 * the floor drops out from under it. Raise it to make comebacks harder.
 */
export let RESILIENCE_EXP = 1.8;
/**
 * The least a function keeps however damaged its buildings are: capability =
 * floor + (1 - floor) x the curve above. Zero is the curve as it stands. A
 * command bunker, airbase or silo field that is completely flattened still
 * does nothing (militaryCapability); everything else, from the factories to the
 * radars, keeps this share of its work while the city stands.
 */
export let CAPABILITY_FLOOR = 0;
// --- accuracy model --------------------------------------------------------
/**
 * cep = baseCep
 *     * (1 + RANGE_CEP_PENALTY * routeUsed/range)
 *     * intelPenalty[intelAtAimpoint]
 *     * (1 + EW_CEP_PENALTY) ^ jamming
 *
 * where jamming counts each jammer the round flew through by how much of it:
 * one for a crossing of the whole bubble or more, less for a graze.
 */
export let RANGE_CEP_PENALTY = 0.45;
export let EW_CEP_PENALTY = 0.45;
/**
 * Multiplier on CEP by intel level at the aimpoint. Blind shots scatter badly.
 *
 * Since a strike shows a building but not what it is (October 2026), most
 * follow-up rounds aim at intel 2. Tried at 1.1 for 2: six-a-side NPC wars
 * finished more often (8 of 12 against 4), three-a-side ones less (12 of 20
 * against 18), and the war is fought three a side; left at 1.35.
 */
export const INTEL_CEP_MULT = { 0: 2.6, 1: 1.9, 2: 1.35, 3: 1.0 };
export const DOCTRINE_SPECS = {
    NEUTRAL: {
        label: 'Balanced',
        defencePk: 1,
        canAttack: true,
        canDefend: true,
        launchMult: 1,
        waivesWaveFloor: false,
        ignoresCommandDamage: false,
        blurb: 'Crews split between the launchers and the guns. No bonus, no hole.',
    },
    OFFENSIVE: {
        label: 'All out',
        defencePk: 0,
        canAttack: true,
        canDefend: false,
        launchMult: 2,
        waivesWaveFloor: true,
        ignoresCommandDamage: false,
        blurb: 'Every hand to the launch bays. Twice the launch slots in every wave, and a wrecked ' +
            'command bunker no longer holds you to one wave. For this round only: your air ' +
            "defence does not fire in this round's exchange.",
    },
    DEFENSIVE: {
        label: 'Dug in',
        // Was 2 until Stage 10 of the strategic layer: a city dug in on its own
        // fourteen stopped 86% of a full pocket's five attackers and a reinforced
        // one 96-100%, survival all but guaranteed. At 1.5 a lone dug-in city is
        // breakable and a reinforced one is still very hard (docs/archive/gameplay/STRATEGIC_LAYER.md).
        defencePk: 1.5,
        canAttack: false,
        canDefend: true,
        launchMult: 1,
        waivesWaveFloor: false,
        ignoresCommandDamage: true,
        blurb: 'Every hand to the guns. Air defence engages half as well again, and a wrecked ' +
            'command bunker no longer thins the belt. For this round only: you ' +
            "launch nothing in this round's exchange.",
    },
};
export const ALL_DOCTRINES = Object.keys(DOCTRINE_SPECS);
export const SIGNAL_SPECS = {
    NEED_DEFENCE: { label: 'Need air defence', short: 'AD', color: '#e8705a', need: true },
    NEED_MUNITIONS: { label: 'Need munitions', short: 'MU', color: '#e8a54a', need: true },
    WINNING: { label: 'Winning here', short: 'WIN', color: '#7fd38a', need: false },
    SPARE_DEFENCE: { label: 'Spare air defence', short: '+AD', color: '#6fc4e8', need: false },
    SPARE_MUNITIONS: { label: 'Spare munitions', short: '+MU', color: '#58c7b4', need: false },
};
export const ALL_SIGNALS = Object.keys(SIGNAL_SPECS);
/** Cards one city can show at once. */
export const MAX_SIGNALS = 3;
/** A client's list made safe: known cards only, no repeats, in a fixed order, at most MAX_SIGNALS. */
export function cleanSignals(input) {
    if (!Array.isArray(input) || input.length > ALL_SIGNALS.length)
        return null;
    if (!input.every((s) => typeof s === 'string' && ALL_SIGNALS.includes(s)))
        return null;
    const set = new Set(input);
    if (set.size > MAX_SIGNALS)
        return null;
    return ALL_SIGNALS.filter((s) => set.has(s));
}
/**
 * Hard ceiling on one submitted salvo.
 *
 * This is a guard against a client sending nonsense, not a rule of the game —
 * the wave slots are what limit a normal salvo, and an all-out city is supposed
 * to be able to throw its entire stockpile in one go. It sat at 40 for a long
 * time, which was low enough to refuse a real salvo from a commander with three
 * waves and full magazines, and the room refuses the whole commitment rather
 * than trimming it: that city then fired nothing at all that phase.
 */
export const MAX_SALVO_ORDERS = 400;
/** A dogleg costs fuel proportional to the extra distance flown. */
export let DOGLEG_FUEL_PER_KM = 0.01;
/**
 * And a fixed charge for every round flown round a waypoint. By the kilometre
 * alone a strike drone's short dogleg cost a fraction of a unit, so sending a
 * whole salvo round the side cost next to nothing; a charge on every round
 * makes it a decision about fuel.
 */
export let DOGLEG_FUEL_PER_ROUND = 4;
/**
 * How far off the target a bearing-picked waypoint sits, as a fraction of the
 * direct flight, with a floor for cities that are neighbours.
 *
 * This is what makes a chosen bearing cost something. Asking to arrive on the
 * bearing your launcher already sits on barely lengthens the flight; coming in
 * from the opposite face costs most of the journey again, and on a short-legged
 * drone it will simply not reach. Attacking the face they are not watching is
 * supposed to be a decision, not a free option.
 */
export let DOGLEG_STANDOFF_FRACTION = 0.45;
export let DOGLEG_STANDOFF_MIN_KM = 80;
export const DEFENCE_SPECS = {
    ciws: {
        label: 'C-RAM',
        radius: 3,
        arc: 360,
        shots: 10,
        // Light enough that one strike drone, one cruise missile or the edge of a
        // ballistic missile's blast destroys it (owner, 21 October 2026; D29: 60).
        // Every launcher went down together so that going for the belt is a tactic.
        hp: 16,
        cost: { materials: 10, fuel: 1, credits: 6 },
        // Close-in, all-round protection. A short SAM has longer reach and a better
        // per-shot drone kill chance, but C-RAM can answer more strike drones from
        // any bearing within its local field. Other munition odds stay unchanged.
        pk: { recon_drone: 0.34, strike_drone: 0.5, cruise: 0.28, ballistic: 0.1, icbm: 0.04 },
        blurb: 'Close-in protection from every bearing. Strong against strike drones; keep it near what matters.',
    },
    sam_short: {
        label: 'Short-Range SAM',
        radius: 6,
        arc: 90,
        shots: 8,
        // D29: 80. Two drones, one cruise missile.
        hp: 28,
        cost: { materials: 16, fuel: 2, credits: 12 },
        // The answer to anything that flies low and slow enough to be tracked all
        // the way in — drones and cruise missiles. It cannot reach a re-entry body
        // and is not meant to.
        // Weighted towards the drone rather than the cruise missile, so that the
        // cruise missile keeps its place in the middle of the three: harder to stop
        // than a drone, easier than a ballistic. The short SAM answers both, but it
        // is the swarm it answers best — across the 90° it is turned to face.
        pk: { recon_drone: 0.75, strike_drone: 0.8, cruise: 0.75, ballistic: 0.1, icbm: 0.03 },
        blurb: 'The workhorse. Drones and cruise missiles, and nothing above them, across a 90° cone.',
    },
    sam_long: {
        label: 'Long-Range SAM',
        radius: 11,
        arc: 60,
        shots: 5,
        // D29: 100. Two drones, one cruise missile; a ballistic missile overkills it.
        hp: 36,
        cost: { materials: 30, fuel: 4, credits: 26 },
        // The quality layer: it engages everything competently and is the only thing
        // that genuinely threatens a ballistic missile. Its price is volume and
        // breadth — five shots a wave, most of them kept for whatever comes in
        // high (HOLD_BACK), out of a magazine of six, across a narrow cone — so it cannot answer
        // mass, nor anything that comes round its side. That trade is the whole
        // shape of the belt: quality here, volume elsewhere.
        //
        // Retuned with the cones (October 2026, scripts/analysis/siege.ts
        // --targets): a well-placed belt turned to the threat stops about nine in
        // ten of a poorly built wave; a commander who reads the cones and comes
        // round them does four times the damage (docs/archive/gameplay/STRATEGIC_LAYER.md).
        //
        // The long-range magazine stays shallow: it is the high-track quality
        // layer, while short SAM and C-RAM provide the sustained volume.
        pk: { recon_drone: 0.55, strike_drone: 0.55, cruise: 0.65, ballistic: 0.7, icbm: 0.3 },
        blurb: 'Engages anything and is the one real answer to a ballistic missile, far out across a ' +
            '60° cone. Five shots a wave, most of them kept for whatever comes in high: quality, never quantity.',
    },
    ew_jammer: {
        label: 'EW Jammer',
        radius: 6,
        arc: 360,
        shots: 99,
        // D29: 50. One drone.
        hp: 16,
        cost: { materials: 12, fuel: 3, credits: 10 },
        // For the jammer these are divert probabilities, not kills: the chance of
        // turning away a round that crosses the whole bubble. A round works on its
        // seeker for as long as it is inside, so one that only grazes the edge, or
        // flies so fast it is through in an instant, gets a fraction of it.
        //
        // A jammer works on every seeker inside its bubble at once, so unlike a
        // launcher it does not care how many are coming — which makes it the one
        // answer to a mass drone raid. An ICBM's re-entry body is on a purely
        // inertial run by the time it is inside that bubble: there is no seeker
        // left to break.
        pk: { recon_drone: 0.55, strike_drone: 0.45, cruise: 0.18, ballistic: 0.02, icbm: 0.01 },
        blurb: 'Destroys nothing and runs out of nothing. Works on every seeker at once, which ' +
            'is what a swarm is made of.',
    },
    shelter: {
        label: 'Civil Shelter',
        radius: 4,
        arc: 360,
        shots: 0,
        hp: 90,
        cost: { materials: 8, fuel: 0, credits: 4 },
        pk: {},
        blurb: 'Buys no ground. Cuts deaths among the people inside its radius.',
    },
};
/**
 * Manning and running a launcher costs something every round. This is what stops
 * a defender simply building air defence until nothing can get through, and it
 * makes the refinery and the power plants worth striking: starve the economy and
 * the belt goes dark on its own.
 */
export const DEFENCE_UPKEEP = {
    ciws: { fuel: 1, credits: 2 },
    sam_short: { fuel: 2, credits: 4 },
    sam_long: { fuel: 4, credits: 9 },
    ew_jammer: { fuel: 3, credits: 3 },
    shelter: { fuel: 0, credits: 1 },
};
export const INTERCEPTOR_TYPES = ['sam_long', 'sam_short', 'ciws', 'ew_jammer'];
export const ALL_DEFENCES = Object.keys(DEFENCE_SPECS);
/**
 * The layers that shoot. Along a corridor, each fires where the round first
 * enters its cover, so the long reach naturally engages first; this order only
 * breaks ties between launchers that pick the round up at the same point.
 */
export const ENGAGEMENT_ORDER = ['sam_long', 'sam_short', 'ciws'];
/** Casualties in the shelter radius are cut by this much. */
export let SHELTER_CASUALTY_REDUCTION = 0.6;
/** ACTIVE posture buys interceptor accuracy at the price of being seen. */
export let ACTIVE_POSTURE_PK_BONUS = 0.15;
// --- fog of war ------------------------------------------------------------
/** Intel decays by this many levels per round if nothing refreshes it. */
export let INTEL_DECAY_PER_ROUND = 1;
/**
 * Rounds an intel level survives before decay ticks it down.
 *
 * Six, not two. A picture does go stale, but a theatre that forgot a city
 * every other round made reconnaissance a treadmill rather than an investment:
 * you re-flew the same drone over the same district forever and never got to
 * spend the knowledge on anything. At six a good look is worth taking, worth
 * pooling, and still gone by the time the city it described has been rebuilt.
 */
export let INTEL_DECAY_INTERVAL = 6;
/**
 * How much of its own picture a city leaks when its radar network is gone.
 *
 * Radar no longer looks outward. See `radarExposure` in fog.ts: these are the
 * fractions of radar capability that have to be destroyed before enemies start
 * seeing the city for free, loud and quiet respectively.
 */
export const RADAR_LEAK_BANDS = { active: [0.25, 0.6], emcon: [0.5, 0.8] };
// --- population ------------------------------------------------------------
export let START_POPULATION = 1040; // thousands, distributed across occupied city fabric
/**
 * Hitpoints of the passive city, per thousand of the starting population,
 * shared equally between every passive building. Flat between buildings on
 * purpose, for now: how big or full a block is does not change how much it
 * takes to bring it down. A tuning point: at 7, a city's 80 blocks get 91
 * each, where the old size-based figure ran 74 (small, low) to 160 (large,
 * tall).
 */
export let URBAN_HP_PER_KPOP = 7;
export let START_FEAR = 8;
export let START_UNREST = 5;
export let START_LOYALTY = 70;
/** Fear added per thousand killed. */
export let FEAR_PER_CASUALTY = 0.55;
/** Fear added per round for each destroyed utility (power, water, hospital). */
export let FEAR_PER_LOST_UTILITY = 3.5;
/** Baseline fear decay in a round with no casualties. */
export let FEAR_DECAY = 4;
/** Unrest moves toward (fear - loyalty) at this rate per round. */
export let UNREST_RATE = 0.28;
/** Unrest falls by this much per round when fear is below loyalty. */
export let UNREST_RECOVERY = 2.5;
/** The loyalty a quiet, unrepressed city drifts back toward. */
export let LOYALTY_BASELINE = 55;
/** Repression added per point of loyalty a control action costs. */
export let REPRESSION_PER_LOYALTY = 0.6;
/** Repression bleeds off this slowly. Grudges outlast the emergency. */
export let REPRESSION_DECAY = 0.35;
/** Fear added per round while the city is visibly in ruins. */
export let FEAR_PER_RUIN = 9;
/**
 * Fear is a shock that fills and discharges rather than a slow bleed.
 *
 * At the top of the scale the city stops: it will still man the guns, because
 * the people on them have nowhere better to be, but nothing leaves it and no
 * plan gets through. Riding that out is also what empties the meter — a city
 * that has already had its worst round is not frightened of the next one in
 * the same way.
 */
export let FEAR_FREEZE_AT = 100;
/** Rounds a city stays frozen once fear fills. */
export let FEAR_FREEZE_ROUNDS = 1;
/**
 * What actually spends a population's support, per round.
 *
 * Loyalty is not a function of fear any more. Fear is a panic that fills and
 * discharges, and hanging the loss condition off it meant a city that froze —
 * and so emptied its fear — stopped losing support at exactly the moment it
 * was being hit hardest. Support is spent by the dead, damage to the broadcast
 * network, and what you have done to people to keep them quiet.
 */
export let LOYALTY_PER_CASUALTY = 0.25;
/**
 * Maximum loyalty lost per round from broadcast mast HP damage, scaled by
 * (1 - mast integrity). A healthy mast costs zero; a destroyed mast costs the
 * full amount. The legacy tuning name preserves existing lab drafts/presets.
 */
export let LOYALTY_PER_RUIN = 2.5;
/** Incoming warnings: bounded volume uncertainty and adjacent compass-sector error. */
export const INCOMING_INTEL = {
    drone: { largeAt: 0.5, uncertainty: 0.15, directionChance: 0.55, directionError: 0.6 },
    cruise: { mediumAt: 0.33, largeAt: 0.66, uncertainty: 0.08, directionChance: 0.85, directionError: 0.25 },
    ballistic: { bandWidth: 3, uncertainty: 1, directionChance: 1, directionError: 0.08 },
};
/** Lost per round per point of standing repression. Holding them down costs. */
export let LOYALTY_PER_REPRESSION = 0.03;
/** People patching up their own homes, and their hospitals (repairHomes). */
export const HOME_REPAIR = {
    /**
     * The share of a damaged block of the city's fabric its residents put back
     * each round, with every resident still living there and loyalty at the
     * peacetime baseline or better. Housing is three quarters of a city's
     * integrity, and integrity is how cities fall, so this is tuned against the
     * pace of the war.
     */
    rate: 0.1,
    /** Named housing blocks recover this share of max HP each round, even from zero. */
    residentialRate: 0.1,
    /** Hospitals recover at this rate while loyalty holds, including from zero. */
    landmarkRate: 0.25,
    /**
     * Below this loyalty hospitals and ordinary blocks stop recovering. Named
     * housing has its own fixed rate. This is also the takeover line.
     */
    loyaltyFloor: 25,
};
/**
 * What a persistent agent does, and it is not damage.
 *
 * A chemical strike leaves the buildings standing and takes the city out of the
 * war for a round: gunnery crews in respirators engage at half effect, and a
 * targeting cell running from a shelter puts its rounds nowhere near where it
 * meant to. Not a device — a city under a cloud comes back. But it is a round
 * of somebody else's war that you get to fight unopposed.
 */
export let CHEMICAL_ROUNDS = 1;
/** Multiplier on the contaminated city's CEP — bigger is less accurate. */
export let CHEMICAL_CEP_MULT = 2;
/** Multiplier on the contaminated city's interception. */
/**
 * How much less a second jammer on the same corridor is worth than the first.
 *
 * A jammer is the one defence with no ammunition and no rate of fire, so
 * without this a belt of them multiplied out into a wall: seven jammers each
 * rolling independently stopped 99% of a drone raid, which made the whole
 * question of what to buy go away. A seeker that has already lost its picture
 * cannot lose it twice, so each further jammer contributes about half of what
 * the one before it did. They all still work on every round — that is what
 * makes them the answer to mass — but the answer converges instead of running
 * to certainty.
 */
export let EW_STACK_FALLOFF = 0.55;
/**
 * The share of a gun's fire it keeps back from each kind of incoming round.
 *
 * A launcher with a magazine does not empty it into whatever crosses it
 * first. At the start of each wave a gun can fire the lesser of its rate and
 * its magazine; against a weapon listed here it keeps this share of that back
 * (rounded up), and fires at it only while it has more than that left. Any
 * shot spends from what is left, so a round it is kept for can use the
 * reserve, and a round it is not can only spend down to it. What is held is
 * not lost: it stays in the magazine for the next wave and the next round.
 *
 * This is what makes the belt layered rather than merely mixed. The long SAM
 * is the one real answer to a ballistic missile and keeps most of its fire for
 * the high tracks; the short SAM keeps some back from drones for the cruise
 * missiles; C-RAM, the volume layer, fires at everything. A cheap raid can
 * only ever draw part of a belt in the wave it flies in.
 *
 * Replaced the single shot the long SAM used to hold for high tracks (18
 * October 2026, owner's direction; docs/DECISIONS.md D15). Starting values, to
 * be tuned on the exchange bench (docs/tooling/LAB.md).
 */
export const HOLD_BACK = {
    sam_long: { recon_drone: 0.6, strike_drone: 0.6, cruise: 0.4, ballistic: 0, icbm: 0 },
    sam_short: { recon_drone: 0.4, strike_drone: 0.4, cruise: 0, ballistic: 0, icbm: 0 },
    ciws: { recon_drone: 0, strike_drone: 0, cruise: 0, ballistic: 0, icbm: 0 },
};
export let CHEMICAL_DEFENCE_MULT = 0.5;
// --- plan phase ------------------------------------------------------------
//
// A round has one plan phase, where it used to have two between its two strike
// phases. So a plan phase carries two of the old ones: twice the action
// points, twice the production lines and twice the income, and what a city can
// repair, build and develop between exchanges is what it always was.
export let BASE_ACTION_POINTS = 12;
/** Command II capstone: four more healthy Plan AP, subject to command damage. */
export let STAFF_ACTION_POINTS = 16;
/**
 * Production lines a healthy city runs in one plan phase, before damage to
 * the factories and the power plants cuts them (productionCapacity).
 */
export let PRODUCTION_LINES = 26;
export const START_RESOURCES = { materials: 420, fuel: 140, credits: 120 };
export const ALL_RESOURCES = ['materials', 'fuel', 'credits'];
/**
 * Turning one of the side's resources into another: a plan order that costs
 * one action point and is paid into the side's treasury like income, so what
 * one commander converts any ally can spend.
 *
 * A side's income does not arrive in the proportions its war uses. Headless
 * ladders before this existed: credits piled up (a median 193 a city left
 * after every plan, 334 by round 13) while a third of plan phases ended with
 * materials or fuel all but gone, and in two thirds of those the side sat on
 * 150+ credits a city it could not use. This is the valve.
 *
 * Every conversion loses a quarter of the value, so a round trip keeps 56%
 * and nothing can be farmed; income stays the main supply, and a factory or
 * refinery still matters. No building limits it: it is staff work, which is
 * what lets a battered city with nothing left to repair still help its side.
 */
export const CONVERSION = {
    ap: 1,
    /** Value that goes in per order: 200 materials or credits, or 100 fuel. */
    batch: 200,
    /** The share of that value that comes out the other side. */
    keep: 0.75,
    /** What a unit of each is worth against the others. Fuel is the scarcest. */
    value: { materials: 1, fuel: 2, credits: 1 },
};
export const START_ARSENAL = {
    recon_drone: 4,
    strike_drone: 8,
    cruise: 3,
    ballistic: 1,
    // None to start. A weapon that reaches the whole board is something a
    // commander decides to build, not something they wake up holding.
    icbm: 0,
};
export const START_DEFENCES = {
    ciws: 4,
    sam_short: 3,
    sam_long: 1,
    ew_jammer: 2,
    shelter: 2,
};
/** Income per round, scaled by the integrity of the producing asset type. */
export const INCOME = {
    // Cut on 20 October 2026 (sprint item 2): money never bound, and a city's
    // treasury held five to ten rounds of income by mid-war. Fuel is left as it
    // was and doglegs priced against it instead.
    materialsPerFactory: 100,
    fuelPerRefinery: 124,
    // Credits once bound everything at once — warheads, development, rebuilding
    // and upkeep all draw on the same purse — and the base was raised. With
    // interception bounties on top they stopped binding at all, so it is lower
    // again, beside a much smaller bounty.
    creditsBase: 100,
    creditsPerPopK: 0.12,
};
/**
 * Paid to the defender for every munition its air defence brings down.
 *
 * A shootdown is worth something in its own right: wreckage to sell, a
 * government with something to show for the money, crews who can be told the
 * belt is working. It also gives a city that spends its whole round defending
 * an income of its own, rather than watching the attacker bank the initiative
 * and the economy both. Priced off what the munition cost to build, so knocking
 * down a ballistic missile pays for itself and swatting a drone does not.
 */
export const INTERCEPT_BOUNTY = {
    // A third of what they were (20 October 2026): a strike drone cost 1 credit
    // to build and paid 8 shot down, and bounties were more than half of every
    // side's credit income.
    recon_drone: 2,
    strike_drone: 2,
    cruise: 6,
    ballistic: 10,
    icbm: 16,
};
// --- development --------------------------------------------------------------
/**
 * A development costs action points and nothing else.
 *
 * It used to take materials and credits per step as well, which meant the track
 * you could least afford to sit on was the one you could never start: a city
 * being ground down had action points spare and no resources, so its points went
 * on nothing at all. Points are now what development is *for* — the thing you spend
 * them on when the stockpile is empty and there is nothing worth building.
 */
export const TIER_1_DEVELOPMENT_COST = 12;
export const TIER_2_DEVELOPMENT_COST = 24;
export const MAX_DEVELOPMENT_AP_PER_PLAN = 4;
export const MAX_TIER_1_DEVELOPMENTS = 2;
export const MAX_TIER_2_DEVELOPMENTS = 1;
export const DEVELOPMENT_SPECS = {
    attack_1: { category: 'Attack', tier: 1, label: 'Surgical Strikes', required: TIER_1_DEVELOPMENT_COST, blurb: '20% less CEP; 25% less dogleg fuel. Permanent attack quality and flexibility.' },
    attack_2: { category: 'Attack', tier: 2, label: 'Escalation', required: TIER_2_DEVELOPMENT_COST, blurb: 'Four standard waves. Storage and slots per wave stay fixed.' },
    defence_1: { category: 'Defence', tier: 1, label: 'Civil Defence', required: TIER_1_DEVELOPMENT_COST, blurb: '+0.08 interceptor kill probability; shelters cover 2 more tiles and reduce casualties by 75%.' },
    defence_2: { category: 'Defence', tier: 2, label: 'Fortress City', required: TIER_2_DEVELOPMENT_COST, blurb: '+4 interceptor establishment and siting capacity; 35% deeper magazines.' },
    command_1: { category: 'Command', tier: 1, label: 'Common Cause', required: TIER_1_DEVELOPMENT_COST, blurb: 'Extended regional reach; offensive stock dispatch with an operational Broadcast Mast.' },
    command_2: { category: 'Command', tier: 2, label: 'War Footing', required: TIER_2_DEVELOPMENT_COST, blurb: 'Healthy Plan AP rises from 12 to 16; command damage still scales it.' },
};
export const DEVELOPMENT_EFFECTS = {
    attack_2_bonus: WAVE_DOCTRINE_BONUS,
    defence_2_magazine: 1.35,
    attack_1_cep: 0.8,
    attack_1_dogleg_fuel: 0.75,
    defence_1_radius: 2,
    defence_1_reduction: 0.75,
    defence_1_pk: 0.08,
    defence_2_slots: 4,
    command_2_ap: STAFF_ACTION_POINTS,
};
export const TIER_2_DEVELOPMENTS = ['attack_2', 'defence_2', 'command_2'];
// --- defeat ----------------------------------------------------------------
/**
 * The share of a city's income a surrendered city smuggles to its side each
 * plan: worth having, never worth more than fighting on.
 */
export let SURRENDER_SHARE = 0.4;
/** Below this fraction of starting asset HP, the city is finished. */
export let DESTRUCTION_THRESHOLD = 0.5;
/**
 * Loyalty at or below this is an uprising, and the city is gone.
 *
 * The defeat condition used to hang off unrest with loyalty as a buffer, which
 * made loyalty a modifier on somebody else's meter rather than a thing in its
 * own right. Now unrest is the pressure and loyalty is the thing being spent:
 * you lose a city when the people in it are finished with you.
 */
export let UPRISING_LOYALTY = 0;
/** Takeover needs loyalty under this, both barracks gone, and no live interceptors. */
export let TAKEOVER_LOYALTY_CEILING = 25;
/**
 * What firing a nuclear device does to the city that fires it (docs/world/LORE.md): its
 * own people see what their side has become. Loyalty and unrest at the launch,
 * whatever becomes of the missile; more loyalty again if it lands. Only the
 * launching city: the reckoning is theirs.
 */
export let NUCLEAR_LAUNCH_LOYALTY = 25;
export let NUCLEAR_LAUNCH_UNREST = 15;
export let NUCLEAR_HIT_LOYALTY = 10;
// --- casualties ------------------------------------------------------------
/**
 * Fraction of a housing block's residents killed when the block is completely
 * destroyed. Deliberately low: this is a game about a population that survives,
 * gets frightened, and eventually turns on you.
 */
export let CASUALTY_FRACTION = 0.15;
/** Casualty reduction per surviving hospital, capped in the resolver. */
export let HOSPITAL_CASUALTY_REDUCTION = 0.12;
// --- strategic layer: experiments -------------------------------------------
//
// Every value the strategic layer introduces or retunes lives here, so a
// balance pass changes one block rather than hunting through the file. They
// are experiments, not settled rules: docs/archive/gameplay/STRATEGIC_LAYER.md records what each was,
// what it became, and why.
/**
 * How one wave's munitions from several attackers are ordered on arrival.
 * 'arrival' sorts the whole wave by each weapon's place (WEAPON_SPECS
 * `arrival`), so every attacker's rounds come in as one package;
 * 'interleave' rotates between attackers as the game used to, each in arrival
 * order, but in hashed rather than submission order.
 */
export let RESOLVE_MERGE = 'arrival';
/** The most a jammer can ever do to one munition: the same ceiling a kill chance has. */
export let MAX_DIVERT_CHANCE = 0.95;
/**
 * How much of the capability lost to partial damage the barracks give back,
 * at full barracks strength, for the command bunker, the airbase and the silo
 * field (spec §4.1). A flattened facility is not helped at all; see
 * `militaryCapability`.
 */
export let BARRACKS_MITIGATION = 0.2;
/**
 * What Common Cause adds to each weapon, in km.
 *
 * Two different upgrades share the name, and the board is built around both:
 *
 *  - Cruise +600: the third enemy of a 4v4 cluster, which cruise cannot reach
 *    without it (REACH_RULE). Cruise stays a local weapon either way, and the
 *    other clusters of a region stand beyond it (`clusterFloorKm`).
 *  - Ballistic and recon +3500, to 6000km: the rest of the region. A region is
 *    four clusters laid out so that every enemy in another of them sits beyond
 *    extended cruise range (2000km) and inside this. It is the regional layer:
 *    a city whose own cluster is still being fought can reach into the rest of
 *    its region with it, and no further (`strikeScope`). A neighbouring
 *    cluster can stand inside unextended ballistic range; the scope rule, not
 *    the distance, is what keeps a ballistic missile home without it.
 *
 * The region layout in `pockets.generated.ts` is solved against these figures
 * by `scripts/solve-pockets.mjs`; change one and re-run the solver.
 *
 * Strike drones get nothing: they are meant to stay local. The ICBM figure is
 * moot, since it already reaches the whole board.
 */
export const EXTENDED_RANGE_ADD_KM = {
    recon_drone: 3500,
    strike_drone: 0,
    cruise: 600,
    ballistic: 3500,
    icbm: 1000,
};
/**
 * How many enemy cities each conventional weapon reaches inside a full 4v4
 * cluster, before and after Common Cause: a strike drone one, a cruise
 * missile two (three extended), a ballistic missile all four. The cluster
 * templates in `pockets.generated.ts` are solved to this by
 * `scripts/solve-pockets.mjs`; change it and re-run the solver.
 *
 * Distance is symmetric, so a city that reaches two enemies with a cruise
 * missile is also reached by exactly two: without Common Cause no three
 * cities of a full cluster can cruise the same one.
 *
 * What Common Cause does for a ballistic missile beyond its own cluster is
 * the regional layer, not this rule: see EXTENDED_RANGE_ADD_KM.
 */
export const REACH_RULE = {
    strike_drone: { base: 1, extended: 1 },
    cruise: { base: 2, extended: 3 },
    ballistic: { base: 4, extended: 4 },
};
/** Clusters in a full region: four 4v4s, sixteen cities a side. */
export const CLUSTERS_PER_REGION = 4;
/**
 * How many interceptors a city may have in service, wherever they are: sited,
 * in its reserve, or on loan to an ally (spec D-01). Production stops at the
 * establishment; only a unit destroyed frees a place, and lending one frees
 * none. The buffer is headroom above it, for tuning.
 */
export let DEFENCE_ESTABLISHMENT = 14;
export let DEFENCE_ESTABLISHMENT_BUFFER = 0;
/**
 * The elastic establishment, for the side that is behind (D19): a city whose
 * cluster is still being fought may have this many more interceptors in
 * service for each city its side is down there, counting the cities alive on
 * each side at the end of the last exchange. It follows the cluster, never
 * the war: a decided cluster, a strategic reserve and a level fight have none.
 * What a city owns past a fallen establishment is kept, inactive, until there
 * is room again (`reconcileEstablishment`). Zero since D20 replaced it with
 * the cut below; two was its value for the day it was the rule.
 */
export let ELASTIC_ESTABLISHMENT_PER_MISSING_CITY = 0;
/**
 * The elastic establishment, for the side that is ahead (D20): a city whose
 * cluster is still being fought has this many fewer interceptors in service
 * for each city its side is up there, and what it owns past the cut is
 * scrapped at random at the round's end (`cullEstablishment`), never to come
 * back. Three by default: a 4v3 holds eleven, a 4v2 eight. It lapses when the
 * cluster is decided, or the lead does, and the city may then build back up.
 * Shelters sit outside the establishment and are never touched. Zero is the
 * fixed establishment.
 */
export let ESTABLISHMENT_CUT_PER_CITY_AHEAD = 3;
/** The establishment is never cut below this many interceptors: one layer. */
export let ESTABLISHMENT_MIN = 4;
/**
 * Civil shelters sit outside the establishment and the siting cap, with an
 * allowance of their own (spec D-04). They cannot be lent.
 */
export let SHELTER_ALLOWANCE = 4;
/**
 * How many interceptors one city can direct at once, at full health (spec
 * D-02). Twice the establishment, so a city's own fourteen always fit and the
 * other half is room for allies' loans: a fully reinforced city is the one
 * that can hold off four or five attackers. Damage to the command bunker and
 * radars thins it (`sitingCap`); Fortress City adds four. Shelters do
 * not count against it.
 */
export let BASE_SITING_CAP = 28;
/**
 * The share of a fallen city's surviving interceptors that go to the rest of
 * its side instead of being lost with it: the healthiest first, each to the
 * ally with the thinnest belt at the time, into its reserve with the health
 * and rounds it had. They belong to no establishment, and each one a city
 * takes in raises its siting cap by one, because the crews come with them.
 * Zero is the old rule: everything a fallen city holds goes with it.
 */
export let FALLEN_DEFENCE_SHARE = 0;
// Rates of fire live with DEFENCE_SPECS above. The organised belt used for
// balance measurements is ORGANISED_MIX in packages/server/src/bot.ts.
/**
 * Launch slots an interceptor takes when it is sent to an ally, out of the
 * missile pool (spec L-01). A long SAM is a battery, not a launcher: it books
 * a silo pool the way a ballistic missile does. Shelters never move.
 */
export const TRANSFER_UNIT_SLOTS = {
    ciws: 3,
    sam_short: 3,
    ew_jammer: 3,
    sam_long: 7,
};
/** Missile slots one loose warhead takes when it is sent to an ally. */
export let TRANSFER_WARHEAD_SLOTS = 1;
/** Guard against a client sending nonsense, like MAX_SALVO_ORDERS. */
export const MAX_TRANSFER_ORDERS = 64;
/** One donor's combined defensive loans to all allies in a round. */
export const DEFENCE_LOAN_LIMIT = 2;
/**
 * What Mass Production adds to one line of each weapon (spec §10, Stage 7).
 *
 * It was a flat +2 on every line, which is proportionally worth far more at
 * the heavy end — a ballistic line going from one missile to three — than at
 * the drone end. Stage 7 measured a per-weapon table against it and this is
 * the one chosen (variant A): +1 a line, and nothing on an ICBM line, where
 * the rate is the weapon's whole balance. The ballistic +1 went the same way:
 * mass production widens the light lines, not the strategic ones. Base lines changed with it (variant
 * B, PRODUCTION_BATCH). `botmatch.mjs --production A|B` reproduces the runs.
 */
// --- tuning ------------------------------------------------------------------
/**
 * Set one of the single-number rules above by name, and report whether it is
 * one. Only tuning.ts calls this, with values it has already checked. The
 * assignment is to the module's own binding, which every importer sees: an ES
 * module export is live, so nothing that reads the rule needs to change.
 */
export function setTunable(name, value) {
    switch (name) {
        case 'BASE_ACTION_POINTS':
            BASE_ACTION_POINTS = value;
            return true;
        case 'STAFF_ACTION_POINTS':
            STAFF_ACTION_POINTS = value;
            return true;
        case 'PRODUCTION_LINES':
            PRODUCTION_LINES = value;
            return true;
        case 'DEFENCE_BATCH':
            DEFENCE_BATCH = value;
            return true;
        case 'PRODUCTION_SURGE_CHANCE':
            PRODUCTION_SURGE_CHANCE = value;
            return true;
        case 'PRODUCTION_SURGE_MULT':
            PRODUCTION_SURGE_MULT = value;
            return true;
        case 'REARM_BASE':
            REARM_BASE = value;
            return true;
        case 'REARM_AP':
            REARM_AP = value;
            return true;
        case 'ICBM_PER_WAVE':
            ICBM_PER_WAVE = value;
            return true;
        case 'BALLISTIC_PER_WAVE':
            BALLISTIC_PER_WAVE = value;
            return true;
        case 'AIRBASE_SLOTS':
            AIRBASE_SLOTS = value;
            return true;
        case 'SILO_SLOTS':
            SILO_SLOTS = value;
            return true;
        case 'STOCKPILE_WAVES':
            STOCKPILE_WAVES = value;
            return true;
        case 'DISCOVERY_CHANCE_HIT':
            DISCOVERY_CHANCE_HIT = value;
            return true;
        case 'DISCOVERY_CHANCE_MISS':
            DISCOVERY_CHANCE_MISS = value;
            return true;
        case 'NUCLEAR_DISCOVERY_START_ROUND':
            NUCLEAR_DISCOVERY_START_ROUND = value;
            return true;
        case 'DISCOVERY_STASH_MIN':
            DISCOVERY_STASH_MIN = value;
            return true;
        case 'DISCOVERY_STASH_MAX':
            DISCOVERY_STASH_MAX = value;
            return true;
        case 'BASE_WAVES':
            BASE_WAVES = value;
            return true;
        case 'WAVE_DOCTRINE_BONUS':
            WAVE_DOCTRINE_BONUS = value;
            return true;
        case 'MULTI_WAVE_COMMAND_FLOOR':
            MULTI_WAVE_COMMAND_FLOOR = value;
            return true;
        case 'DOGLEG_FUEL_PER_KM':
            DOGLEG_FUEL_PER_KM = value;
            return true;
        case 'DOGLEG_FUEL_PER_ROUND':
            DOGLEG_FUEL_PER_ROUND = value;
            return true;
        case 'DOGLEG_STANDOFF_FRACTION':
            DOGLEG_STANDOFF_FRACTION = value;
            return true;
        case 'DOGLEG_STANDOFF_MIN_KM':
            DOGLEG_STANDOFF_MIN_KM = value;
            return true;
        case 'THERMOBARIC_RESIDENTIAL_CASUALTIES':
            THERMOBARIC_RESIDENTIAL_CASUALTIES = value;
            return true;
        case 'CHEMICAL_ROUNDS':
            CHEMICAL_ROUNDS = value;
            return true;
        case 'CHEMICAL_CEP_MULT':
            CHEMICAL_CEP_MULT = value;
            return true;
        case 'CHEMICAL_DEFENCE_MULT':
            CHEMICAL_DEFENCE_MULT = value;
            return true;
        case 'SHELTER_CASUALTY_REDUCTION':
            SHELTER_CASUALTY_REDUCTION = value;
            return true;
        case 'ACTIVE_POSTURE_PK_BONUS':
            ACTIVE_POSTURE_PK_BONUS = value;
            return true;
        case 'EW_STACK_FALLOFF':
            EW_STACK_FALLOFF = value;
            return true;
        case 'MAX_DIVERT_CHANCE':
            MAX_DIVERT_CHANCE = value;
            return true;
        case 'RESILIENCE_EXP':
            RESILIENCE_EXP = value;
            return true;
        case 'CAPABILITY_FLOOR':
            CAPABILITY_FLOOR = value;
            return true;
        case 'RANGE_CEP_PENALTY':
            RANGE_CEP_PENALTY = value;
            return true;
        case 'EW_CEP_PENALTY':
            EW_CEP_PENALTY = value;
            return true;
        case 'DESTRUCTION_THRESHOLD':
            DESTRUCTION_THRESHOLD = value;
            return true;
        case 'CASUALTY_FRACTION':
            CASUALTY_FRACTION = value;
            return true;
        case 'HOSPITAL_CASUALTY_REDUCTION':
            HOSPITAL_CASUALTY_REDUCTION = value;
            return true;
        case 'RESOLVE_MERGE':
            RESOLVE_MERGE = value;
            return true;
        case 'INTEL_DECAY_PER_ROUND':
            INTEL_DECAY_PER_ROUND = value;
            return true;
        case 'INTEL_DECAY_INTERVAL':
            INTEL_DECAY_INTERVAL = value;
            return true;
        case 'START_POPULATION':
            START_POPULATION = value;
            return true;
        case 'URBAN_HP_PER_KPOP':
            URBAN_HP_PER_KPOP = value;
            return true;
        case 'START_FEAR':
            START_FEAR = value;
            return true;
        case 'START_UNREST':
            START_UNREST = value;
            return true;
        case 'START_LOYALTY':
            START_LOYALTY = value;
            return true;
        case 'FEAR_PER_CASUALTY':
            FEAR_PER_CASUALTY = value;
            return true;
        case 'FEAR_PER_LOST_UTILITY':
            FEAR_PER_LOST_UTILITY = value;
            return true;
        case 'FEAR_DECAY':
            FEAR_DECAY = value;
            return true;
        case 'UNREST_RATE':
            UNREST_RATE = value;
            return true;
        case 'UNREST_RECOVERY':
            UNREST_RECOVERY = value;
            return true;
        case 'LOYALTY_BASELINE':
            LOYALTY_BASELINE = value;
            return true;
        case 'REPRESSION_PER_LOYALTY':
            REPRESSION_PER_LOYALTY = value;
            return true;
        case 'REPRESSION_DECAY':
            REPRESSION_DECAY = value;
            return true;
        case 'FEAR_PER_RUIN':
            FEAR_PER_RUIN = value;
            return true;
        case 'FEAR_FREEZE_AT':
            FEAR_FREEZE_AT = value;
            return true;
        case 'FEAR_FREEZE_ROUNDS':
            FEAR_FREEZE_ROUNDS = value;
            return true;
        case 'LOYALTY_PER_CASUALTY':
            LOYALTY_PER_CASUALTY = value;
            return true;
        case 'LOYALTY_PER_RUIN':
            LOYALTY_PER_RUIN = value;
            return true;
        case 'LOYALTY_PER_REPRESSION':
            LOYALTY_PER_REPRESSION = value;
            return true;
        case 'UPRISING_LOYALTY':
            UPRISING_LOYALTY = value;
            return true;
        case 'NUCLEAR_LAUNCH_LOYALTY':
            NUCLEAR_LAUNCH_LOYALTY = value;
            return true;
        case 'NUCLEAR_LAUNCH_UNREST':
            NUCLEAR_LAUNCH_UNREST = value;
            return true;
        case 'NUCLEAR_HIT_LOYALTY':
            NUCLEAR_HIT_LOYALTY = value;
            return true;
        case 'TAKEOVER_LOYALTY_CEILING':
            TAKEOVER_LOYALTY_CEILING = value;
            return true;
        case 'SALVAGE_MATERIAL_FRACTION':
            SALVAGE_MATERIAL_FRACTION = value;
            return true;
        case 'SALVAGE_DIVERT_MULT':
            SALVAGE_DIVERT_MULT = value;
            return true;
        case 'SALVAGE_INTERCEPT_MULT':
            SALVAGE_INTERCEPT_MULT = value;
            return true;
        case 'BARRACKS_MITIGATION':
            BARRACKS_MITIGATION = value;
            return true;
        case 'DEFENCE_ESTABLISHMENT':
            DEFENCE_ESTABLISHMENT = value;
            return true;
        case 'DEFENCE_ESTABLISHMENT_BUFFER':
            DEFENCE_ESTABLISHMENT_BUFFER = value;
            return true;
        case 'ELASTIC_ESTABLISHMENT_PER_MISSING_CITY':
            ELASTIC_ESTABLISHMENT_PER_MISSING_CITY = value;
            return true;
        case 'ESTABLISHMENT_CUT_PER_CITY_AHEAD':
            ESTABLISHMENT_CUT_PER_CITY_AHEAD = value;
            return true;
        case 'ESTABLISHMENT_MIN':
            ESTABLISHMENT_MIN = value;
            return true;
        case 'SHELTER_ALLOWANCE':
            SHELTER_ALLOWANCE = value;
            return true;
        case 'BASE_SITING_CAP':
            BASE_SITING_CAP = value;
            return true;
        case 'FALLEN_DEFENCE_SHARE':
            FALLEN_DEFENCE_SHARE = value;
            return true;
        case 'SURRENDER_SHARE':
            SURRENDER_SHARE = value;
            return true;
        case 'TRANSFER_WARHEAD_SLOTS':
            TRANSFER_WARHEAD_SLOTS = value;
            return true;
        default: return false;
    }
}
/** The single-number rules setTunable knows, in file order. */
export const TUNABLE_SCALARS = [
    'BASE_ACTION_POINTS',
    'STAFF_ACTION_POINTS',
    'PRODUCTION_LINES',
    'DEFENCE_BATCH',
    'PRODUCTION_SURGE_CHANCE',
    'PRODUCTION_SURGE_MULT',
    'REARM_BASE',
    'REARM_AP',
    'ICBM_PER_WAVE',
    'BALLISTIC_PER_WAVE',
    'AIRBASE_SLOTS',
    'SILO_SLOTS',
    'STOCKPILE_WAVES',
    'DISCOVERY_CHANCE_HIT',
    'DISCOVERY_CHANCE_MISS',
    'NUCLEAR_DISCOVERY_START_ROUND',
    'DISCOVERY_STASH_MIN',
    'DISCOVERY_STASH_MAX',
    'BASE_WAVES',
    'WAVE_DOCTRINE_BONUS',
    'MULTI_WAVE_COMMAND_FLOOR',
    'DOGLEG_FUEL_PER_KM',
    'DOGLEG_FUEL_PER_ROUND',
    'DOGLEG_STANDOFF_FRACTION',
    'DOGLEG_STANDOFF_MIN_KM',
    'THERMOBARIC_RESIDENTIAL_CASUALTIES',
    'CHEMICAL_ROUNDS',
    'CHEMICAL_CEP_MULT',
    'CHEMICAL_DEFENCE_MULT',
    'SHELTER_CASUALTY_REDUCTION',
    'ACTIVE_POSTURE_PK_BONUS',
    'EW_STACK_FALLOFF',
    'MAX_DIVERT_CHANCE',
    'RESILIENCE_EXP',
    'CAPABILITY_FLOOR',
    'RANGE_CEP_PENALTY',
    'EW_CEP_PENALTY',
    'DESTRUCTION_THRESHOLD',
    'CASUALTY_FRACTION',
    'HOSPITAL_CASUALTY_REDUCTION',
    'RESOLVE_MERGE',
    'INTEL_DECAY_PER_ROUND',
    'INTEL_DECAY_INTERVAL',
    'START_POPULATION',
    'URBAN_HP_PER_KPOP',
    'START_FEAR',
    'START_UNREST',
    'START_LOYALTY',
    'FEAR_PER_CASUALTY',
    'FEAR_PER_LOST_UTILITY',
    'FEAR_DECAY',
    'UNREST_RATE',
    'UNREST_RECOVERY',
    'LOYALTY_BASELINE',
    'REPRESSION_PER_LOYALTY',
    'REPRESSION_DECAY',
    'FEAR_PER_RUIN',
    'FEAR_FREEZE_AT',
    'FEAR_FREEZE_ROUNDS',
    'LOYALTY_PER_CASUALTY',
    'LOYALTY_PER_RUIN',
    'LOYALTY_PER_REPRESSION',
    'UPRISING_LOYALTY',
    'NUCLEAR_LAUNCH_LOYALTY',
    'NUCLEAR_LAUNCH_UNREST',
    'NUCLEAR_HIT_LOYALTY',
    'TAKEOVER_LOYALTY_CEILING',
    'SALVAGE_MATERIAL_FRACTION',
    'SALVAGE_DIVERT_MULT',
    'SALVAGE_INTERCEPT_MULT',
    'BARRACKS_MITIGATION',
    'DEFENCE_ESTABLISHMENT',
    'DEFENCE_ESTABLISHMENT_BUFFER',
    'ELASTIC_ESTABLISHMENT_PER_MISSING_CITY',
    'ESTABLISHMENT_CUT_PER_CITY_AHEAD',
    'ESTABLISHMENT_MIN',
    'SHELTER_ALLOWANCE',
    'BASE_SITING_CAP',
    'FALLEN_DEFENCE_SHARE',
    'SURRENDER_SHARE',
    'TRANSFER_WARHEAD_SLOTS',
];
//# sourceMappingURL=constants.js.map